import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../utils/cn';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  position?: 'top' | 'right' | 'bottom' | 'left';
  delay?: number;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  delay = 0,
  className
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsVisible(true), delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible && childRef.current && tooltipRef.current) {
      const childRect = childRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      let x = 0;
      let y = 0;
      
      switch (position) {
        case 'top':
          x = childRect.left + childRect.width / 2 - tooltipRect.width / 2;
          y = childRect.top - tooltipRect.height - 8;
          break;
        case 'right':
          x = childRect.right + 8;
          y = childRect.top + childRect.height / 2 - tooltipRect.height / 2;
          break;
        case 'bottom':
          x = childRect.left + childRect.width / 2 - tooltipRect.width / 2;
          y = childRect.bottom + 8;
          break;
        case 'left':
          x = childRect.left - tooltipRect.width - 8;
          y = childRect.top + childRect.height / 2 - tooltipRect.height / 2;
          break;
      }
      
      setCoords({ x, y });
    }
  }, [isVisible, position]);

  return (
    <>
      <div 
        ref={childRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-block"
      >
        {children}
      </div>
      
      {isVisible && (
        <div
          ref={tooltipRef}
          className={cn(
            "fixed z-50 p-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg max-w-xs",
            className
          )}
          style={{
            left: `${coords.x}px`,
            top: `${coords.y}px`,
            transform: `translate(${position === 'left' ? '-8px' : position === 'right' ? '8px' : '0'}, ${position === 'top' ? '-8px' : position === 'bottom' ? '8px' : '0'})`,
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.2s, transform 0.2s'
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {content}
        </div>
      )}
    </>
  );
};